import { cva } from 'class-variance-authority';
import MGCompatibilityIcon from '@/components/MGCompatabilityIcon';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { cn } from '@/lib/utils';
import { compatibilityLabels, compatibilityLabelShortnames } from './compatibilityLabels';
import { getCompatibilityVersionText } from './getCompatibilityVersionText';
import type { CompatibilityType } from '@/components/TypesenseFilters/types';
import type { MGCompatibility } from '@music-vine/setlist';
import type { VariantProps } from 'class-variance-authority';
const compatibilityContainerVariants = cva('flex items-center gap-2', {
  variants: {
    layout: {
      default: '',
      'file-size-with-version': 'items-start gap-2.5'
    }
  },
  defaultVariants: {
    layout: 'default'
  }
});
export const compatibilityContentContainerVariants = cva('', {
  variants: {
    layout: {
      default: '',
      'file-size-with-version': 'flex flex-col md:gap-0.5'
    }
  },
  defaultVariants: {
    layout: 'default'
  }
});
export const compatibilityTitleVariants = cva('text-inherit', {
  variants: {
    layout: {
      default: '',
      'file-size-with-version': 'line-clamp-1 font-semibold text-base text-gray-950 leading-tight dark:text-gray-50'
    },
    variant: {
      default: '',
      globalSearch: 'text-xs!'
    },
    size: {
      default: 'text-base md:text-sm',
      sm: 'text-sm',
      xs: 'text-xs'
    }
  },
  defaultVariants: {
    layout: 'default',
    variant: 'default'
  }
});
const compatibilityIconVariants = cva('', {
  variants: {
    size: {
      default: 'size-6',
      sm: 'size-5',
      xs: 'size-4'
    },
    layout: {
      default: '',
      'file-size-with-version': 'size-6 self-center'
    }
  },
  defaultVariants: {
    size: 'default',
    layout: 'default'
  }
});
interface MGCompatibilityDisplayProps extends VariantProps<typeof compatibilityIconVariants> {
  className?: string;
  compatibility: MGCompatibility;
  showVersion?: boolean;
  useFullLabel?: boolean;
  variant?: 'default' | 'globalSearch';
  version?: string;
}
const MGCompatibilityVersion = ({
  version,
  showVersion,
  compatibility
}: {
  version: MGCompatibilityDisplayProps['version'];
  showVersion: MGCompatibilityDisplayProps['showVersion'];
  compatibility: MGCompatibilityDisplayProps['compatibility'];
}) => {
  const {
    isSm
  } = useTWScreens();
  if (!showVersion) {
    return null;
  }
  return <div className="text-gray-700 text-sm dark:text-gray-200" data-sentry-component="MGCompatibilityVersion" data-sentry-source-file="index.tsx">
      {version ?? getCompatibilityVersionText(Number(compatibility.id) as CompatibilityType, !isSm)}
    </div>;
};
const MGCompatibilityDisplay = ({
  compatibility,
  size,
  className,
  version,
  showVersion,
  layout,
  useFullLabel = false,
  variant = 'default'
}: MGCompatibilityDisplayProps) => {
  const {
    isSm
  } = useTWScreens();
  if (!compatibility) {
    return null;
  }
  const compatibilityLabel = compatibilityLabels[Number(compatibility.id) as keyof typeof compatibilityLabels];
  const compatibilityLabelShortname = compatibilityLabelShortnames[Number(compatibility.id) as keyof typeof compatibilityLabelShortnames];
  const label = (isSm ? compatibilityLabel : compatibilityLabelShortname) ?? compatibility;

  // Use the full compatibility label if useFullLabel is true
  const displayLabel = useFullLabel ? compatibilityLabel ?? compatibility : label;
  return <div className={cn(compatibilityContainerVariants({
    layout
  }), className)} data-sentry-component="MGCompatibilityDisplay" data-sentry-source-file="index.tsx">
      <MGCompatibilityIcon className={cn(compatibilityIconVariants({
      size,
      layout
    }))} compatibility={Number(compatibility.id) as CompatibilityType} data-sentry-element="MGCompatibilityIcon" data-sentry-source-file="index.tsx" />

      <span className={cn(compatibilityContentContainerVariants({
      layout
    }))}>
        <div className={cn(compatibilityTitleVariants({
        layout,
        variant,
        size
      }))}>
          {displayLabel}
        </div>
        <MGCompatibilityVersion compatibility={compatibility} showVersion={showVersion} version={version} data-sentry-element="MGCompatibilityVersion" data-sentry-source-file="index.tsx" />
      </span>
    </div>;
};
export default MGCompatibilityDisplay;